import { useMemo, useState } from 'react';
import { UserMinus } from 'lucide-react';
import type { ConversationDetail } from '../api/conversations';
import { conversationsApi } from '../api/conversations';
import type { RosterRow } from '../api/auth';
import { ComplianceRefusal } from '../api/conversations';
import { ApiError } from '../api/client';
import { Overlay } from './Overlay';
import { RosterPicker, initialsOf } from './RosterPicker';
import { Avatar, ParticipantLegend } from './ParticipantChip';

/**
 * Everyone who can see the thread, in full — and, for whoever may change it, the controls.
 *
 * The strip shows a handful of chips and a count; this is where the enumeration lives. Rule 7 asks
 * that no read path go undisclosed — it does not ask that ninety names sit permanently on screen,
 * and a caption sentence nine hundred characters long is disclosure that nobody reads.
 *
 * Four groups, and the **grouping is what carries the difference**. Guardian readers get ordinary
 * adult chips rather than a shape of their own: the design system spends shape on youth versus
 * adult, and a third shape would overload the one signal that already means something specific.
 * What separates them is the heading, exactly as "Monitored by" is separated today.
 *
 * **"Read by oversight" is the fifth, and it is the sharpest one on the page** (TROOP-14). I3 lets
 * a unit admin read a thread they are not in; I7 and rule 7 say the thread must show that it
 * happened and who did it. It sits last because it is not about who is here — it is about who has
 * looked — and it is shown to everybody who can read the thread, youth included. A young person is
 * entitled to know an adult who was not in their conversation has read it, and softening that into
 * a count with no name would be the disclosure failing at exactly the point it matters.
 *
 * **"No longer here" is the fourth, and it is rule 7 rather than a nicety** (TROOP-178). A list
 * that quietly loses somebody is the same class of problem as one that quietly gains a hidden
 * observer. Their messages stay in the stream under their own name (rule 6), so withholding this
 * would leave an author nobody on the page can account for — and it is shown to everybody who can
 * read the thread, youth included, which is what I7 means by disclosed.
 *
 * **Only an explicit participant carries a remove control, and that is structural rather than a
 * decision this component makes.** A derived person has no row: the roster puts them there, an
 * officer's post does, or their own child does. Rendering a disabled control beside them would owe
 * an explanation on every row; rendering none says the true thing, which is that the way to change
 * it is somewhere else entirely.
 */
export function ParticipantsPanel({
  detail,
  unitId,
  onChanged,
  onClose,
}: {
  detail: ConversationDetail;
  /**
   * Undefined on the guardian's door, where there is no `/units/` route to call and no office to
   * hold — so the controls are simply absent rather than rendered and refused.
   */
  unitId?: string;
  /** Re-reads the thread after a change, so the lists here are never one edit behind. */
  onChanged?: () => void;
  onClose: () => void;
}) {
  const active = detail.participants.filter((p) => p.mode === 'Active');
  const monitors = detail.participants.filter((p) => p.mode === 'Monitor');
  const former = detail.formerParticipants ?? [];
  const oversight = detail.oversightReads ?? [];

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const canAdd = Boolean(unitId) && detail.canAddParticipants === true;
  const canRemove = Boolean(unitId) && detail.canRemoveParticipants === true;

  // Hoisted out of the JSX below. Computing it where it is passed would put a hook behind
  // `canAdd &&`, which is a conditional call -- React would lose the hook order the first time
  // somebody's permission answer differed between two renders of this panel.
  const alreadyHere = useMemo(
    () =>
      new Set([
        ...detail.participants.map((p) => p.personId),
        ...detail.guardianReaders.map((g) => g.personId),
      ]),
    [detail],
  );

  const add = async (row: RosterRow) => {
    if (!unitId) return;

    setError(null);
    setBusy(row.personId);

    try {
      await conversationsApi.addParticipant(unitId, detail.id, row.personId);
      onChanged?.();
    } catch (e) {
      setError(refusalText(e, `Could not add ${row.displayName}.`));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (personId: string, name: string) => {
    if (!unitId) return;

    setError(null);
    setBusy(personId);

    try {
      await conversationsApi.removeParticipant(unitId, detail.id, personId);
      onChanged?.();
    } catch (e) {
      setError(refusalText(e, `Could not remove ${name}.`));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Overlay title="Everyone here" onClose={onClose}>
      <div className="flex flex-col" style={{ gap: 'var(--space-5)' }}>
        {error && (
          <p
            className="t-body"
            role="alert"
            style={{
              color: 'var(--color-text-primary)',
              background: 'var(--color-status-blocked-surface)',
              border: '1px solid var(--color-status-blocked)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3) var(--space-4)',
              margin: 0,
            }}
          >
            {error}
          </p>
        )}

        {/* Above the lists, not below them. A whole-unit thread on the dev troop puts 499 people in
            the first group, so an add control after them is 22,000px down the page -- present in
            the DOM and unreachable in practice. It is the action; the lists are reference. */}
        {canAdd && unitId && (
          <section>
            <h3
              className="t-label"
              style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-3)' }}
            >
              Add somebody
            </h3>

            {/* One definition of "search the roster and pick somebody", shared with the topic form
                (TROOP-180). It pre-judges nothing -- the server runs the quorum, the Pack rule and
                I5, and each refusal arrives with a sentence this panel renders above. */}
            <RosterPicker
              unitId={unitId}
              exclude={alreadyHere}
              busyPersonId={busy}
              onPick={(row) => void add(row)}
            />
          </section>
        )}

        {active.length > 0 && (
          <Group
            title="In this thread"
            count={active.length}
            people={active.map((p) => ({
              id: p.personId,
              name: p.displayName,
              youth: p.classification === 'Youth',
              // Derived presence has no row, so there is nothing to remove and no control.
              onRemove: canRemove && !p.derived ? () => remove(p.personId, p.displayName) : undefined,
              busy: busy === p.personId,
            }))}
          />
        )}

        {monitors.length > 0 && (
          <Group
            title="Monitored by"
            count={monitors.length}
            note="Here because of their role in the unit. They can read and post like anyone else."
            people={monitors.map((p) => ({
              id: p.personId,
              name: p.displayName,
              youth: p.classification === 'Youth',
              onRemove: canRemove && !p.derived ? () => remove(p.personId, p.displayName) : undefined,
              busy: busy === p.personId,
            }))}
          />
        )}

        {detail.guardianReaders.length > 0 && (
          <Group
            title="Can also read this"
            count={detail.guardianReaders.length}
            note="Parents and guardians of the young people here. Reading comes with being their
                  parent; they are not in the thread and cannot post to it."
            // Always adults: a guardian relationship is held by an adult. The shape says so.
            people={detail.guardianReaders.map((g) => ({
              id: g.personId,
              name: g.displayName,
              youth: false,
            }))}
          />
        )}

        {former.length > 0 && (
          <Group
            title="No longer here"
            count={former.length}
            note="They were in this thread and have been taken out. What they wrote before then is
                  still in it, under their name."
            people={former.map((p) => ({
              id: p.personId,
              name: p.displayName,
              youth: p.classification === 'Youth',
              meta: p.removedBy
                ? `Removed by ${p.removedBy} · ${onDay(p.removedAt)}`
                : `Removed ${onDay(p.removedAt)}`,
              faded: true,
            }))}
          />
        )}

        {oversight.length > 0 && (
          <Group
            title="Read by oversight"
            count={oversight.length}
            note="Unit admins can open any thread in the unit. They are not in this one and cannot
                  post to it. Whenever one of them reads it, that is recorded here."
            // Always adults: only an adult can hold Admin, and the shape says so. No remove
            // control -- there is nothing to remove, and reading is not membership.
            people={oversight.map((o) => ({
              id: o.personId,
              name: o.displayName,
              youth: false,
              meta:
                o.times === 1
                  ? `Read this on ${onDay(o.lastReadAt)}`
                  : `Read this ${o.times} times, most recently ${onDay(o.lastReadAt)}`,
            }))}
          />
        )}

        {/* Mandatory wherever chips appear, and the panel is no exception -- the shape language is
            only usable if it is decoded on the same screen. */}
        <ParticipantLegend />
      </div>
    </Overlay>
  );
}

interface PersonRow {
  id: string;
  name: string;
  youth: boolean;
  /** A second line — when somebody left, and who took them out. */
  meta?: string;
  faded?: boolean;
  onRemove?: () => void;
  busy?: boolean;
}

/**
 * How many of a group are drawn before the rest are put behind a control.
 *
 * A whole-unit thread on the seeded troop has **499** people in its first group, and this panel
 * drew every one of them -- roughly 22,000px of names, which is the failure CLAUDE.md names three
 * separate times (`/family`, subgroups, a parent's children). Rule 7 asks that no read path go
 * undisclosed; it does not ask that four hundred names sit on screen at once, and the true count
 * stays in the heading either way -- the same trade `ParticipantStrip` makes to get here.
 *
 * Bounded rather than truncated: every name is still reachable, one tap away.
 */
const SHOWN_PER_GROUP = 12;

function Group({
  title,
  count,
  note,
  people,
}: {
  title: string;
  count: number;
  note?: string;
  people: PersonRow[];
}) {
  const [all, setAll] = useState(false);
  const drawn = all ? people : people.slice(0, SHOWN_PER_GROUP);
  const hidden = people.length - drawn.length;

  return (
    <section>
      <h3 className="t-label" style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}>
        {title} &middot; <span className="tabular">{count}</span>
      </h3>

      {note && (
        <p
          className="t-caption"
          style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-3)', maxWidth: '44ch' }}
        >
          {note}
        </p>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {drawn.map((person) => (
          // Keyed on the id rather than the name. Names collide -- a unit with two Emmas is
          // ordinary, and a key on the name would make React reuse the wrong row.
          <li
            key={person.id}
            className="flex items-center gap-3"
            style={{ minHeight: 'var(--tap-min)' }}
          >
            <Avatar
              initials={initialsOf(person.name)}
              classification={person.youth ? 'youth' : 'adult'}
            />

            <span className="flex-1 min-w-0">
              <span
                className="t-body block"
                style={{
                  color: person.faded
                    ? 'var(--color-text-secondary)'
                    : 'var(--color-text-primary)',
                }}
              >
                {person.name}
              </span>
              {person.meta && (
                <span
                  className="t-caption block"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {person.meta}
                </span>
              )}
            </span>

            <span className="sr-only">{person.youth ? '(youth)' : '(adult)'}</span>

            {person.onRemove && (
              <button
                type="button"
                className="link-button t-caption inline-flex items-center gap-1"
                onClick={person.onRemove}
                aria-label={`Remove ${person.name} from this thread`}
                style={{ minHeight: 'var(--tap-min)', whiteSpace: 'nowrap' }}
              >
                <UserMinus size={15} aria-hidden="true" />
                {person.busy ? 'Removing' : 'Remove'}
              </button>
            )}
          </li>
        ))}
      </ul>

      {hidden > 0 && (
        <p style={{ margin: 'var(--space-2) 0 0' }}>
          <button
            type="button"
            className="link-button t-caption"
            onClick={() => setAll(true)}
            style={{ minHeight: 'var(--tap-min)' }}
          >
            Show the other {hidden}
          </button>
        </p>
      )}
    </section>
  );
}

/**
 * The server's own sentence, always.
 *
 * Every refusal on these two routes names something somebody can act on — the two-adult quorum,
 * that this person has no row to take away and what would actually change it, that a Pack holds no
 * young members. Replacing any of them with "could not add" throws away the only useful part, and
 * `fallback` is reached only when the request never got an answer at all.
 *
 * A `ComplianceRefusal` carries its title separately, and the title is the half that names the
 * rule — so both are rendered rather than just the message.
 */
function refusalText(error: unknown, fallback: string): string {
  if (error instanceof ComplianceRefusal) {
    return error.message ? `${error.title} — ${error.message}` : error.title;
  }

  return error instanceof ApiError ? error.message : fallback;
}

/** "3 Sep 2026". A removal is a date rather than a time — nobody needs the minute. */
function onDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
